import { Injectable } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { HTTP_INTERCEPTORS, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { BehaviorSubject, Observable, throwError } from "rxjs";
import { catchError, filter, switchMap, take } from "rxjs/operators";

import { ToastrService } from "ngx-toastr";
import { AuthService } from "../service/auth.service";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ModalService } from "../service/modal.service";

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  lastShownOfflineError: Date = null;
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  /**
   * @param {Router} _router
   * @param {AuthService} _authenticationService
   * @param toastrService
   * @param modal
   * @param _route
   * @param modalService
   */
  constructor(
    private _router: Router,
    private _authenticationService: AuthService,
    private toastrService: ToastrService,
    private modal: NgbModal,
    private _route: ActivatedRoute,
    private modalService: ModalService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    request = request.clone({
      setHeaders: {
        "nestjs-language": "de",
      },
    });

    return next.handle(request).pipe(
      catchError((err) => {
        return this.handleError(err, request, next);
      })
    );
  }

  private handleError(err, request: HttpRequest<any>, next: HttpHandler, authHandled = false): Observable<any> {
    if (err.status === 504 || err.status === 503 || err.status === 502) {
      this.modal.dismissAll();
      const returnUrl = this._route.snapshot.queryParams["returnUrl"] || this._router.url;
      if (err.status === 503) {
        this._router.navigate(["loading"], {
          queryParams: { returnUrl },
        });
      } else {
        this._router.navigate(["update"], {
          queryParams: { returnUrl },
        });
      }
      return throwError(err);
    } else if (err.status === 401) {
      if (!authHandled) {
        return this.handleAuthError(request, next, err);
      }
    } else if (err.status === 409) {
      this.modalService.openOptimisticLockingModal();
    }
    let parsedError;
    try {
      if (err.error instanceof Blob) {
        err.error.text().then((errorMessage) => {
          this.showErrorToast(JSON.parse(errorMessage), err);
        });
        return throwError(err);
      } else {
        parsedError = JSON.parse(err.error);
      }
    } catch (e) {
      parsedError = err.error;
    }
    this.showErrorToast(parsedError, err);
    return throwError(err);
  }

  private showErrorToast(parsedError, err) {
    const errorMessage =
      parsedError?.message?.message ??
      parsedError?.message ??
      "Es ist etwas schiefgelaufen! Bitte kontaktiere den Support!";
    const validationMessages = err.error?.errors ?? [];
    const validationString = validationMessages.join("<br/>\n");

    if (errorMessage === "Internal server error") {
      this.toastrService.error("Bitte geben Sie einem System-Administrator Bescheid!", "Es ist etwas schiefgelaufen!", {
        enableHtml: true,
      });
    } else if (validationString) {
      this.toastrService.error(validationString, errorMessage, {
        enableHtml: true,
      });
    } else {
      this.toastrService.error(errorMessage, "Es ist etwas schiefgelaufen!", { enableHtml: true });
    }
  }

  private handleAuthError(request: HttpRequest<any>, next: HttpHandler, error) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this._authenticationService.refreshAccessToken().pipe(
        switchMap(() => {
          this.isRefreshing = false;

          this.refreshTokenSubject.next(true);

          return next.handle(request);
        }),
        catchError((err) => {
          this.isRefreshing = false;

          return this.handleError(err, request, next, true);
        })
      );
    }

    if (request.url.includes("auth/refresh")) {
      error.error.text().then((errorMessage: string) => {
        this.showErrorToast(JSON.parse(errorMessage), error);
      });
      return throwError(error);
    }

    return this.refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap(() => next.handle(request))
    );
  }
}

export const errorInterceptorProvider = {
  provide: HTTP_INTERCEPTORS,
  useClass: ErrorInterceptor,
  multi: true,
};
